(() => {
  if (window.__oneciLandingBgExclusiveBooted) {
    return
  }

  window.__oneciLandingBgExclusiveBooted = true

  const TARGET_SELECTOR = '.main-slider_animated .container-wrapper'
  const GRADIENT_CLASS_RE = /^gr-bg-/
  const SOLID_BG_CLASS_RE = /^g-bg-/

  const getClassList = (node) => {
    return (node.className || '')
      .split(/\s+/)
      .map((item) => item.trim())
      .filter(Boolean)
  }

  const resolveTarget = (node) => {
    if (!(node instanceof HTMLElement)) {
      return null
    }

    if (node.matches(TARGET_SELECTOR)) {
      return node
    }

    return node.closest(TARGET_SELECTOR)
  }

  const hasGradientClass = (node) => {
    return getClassList(node).some((className) => GRADIENT_CLASS_RE.test(className))
  }

  const hasSolidBgClass = (node) => {
    return getClassList(node).some((className) => SOLID_BG_CLASS_RE.test(className))
  }

  const getAddedClasses = (node, oldValue) => {
    const current = getClassList(node)
    const previous = (oldValue || '')
      .split(/\s+/)
      .map((item) => item.trim())
      .filter(Boolean)

    return current.filter((className) => !previous.includes(className))
  }

  const removeGradientClasses = (node) => {
    getClassList(node).forEach((className) => {
      if (GRADIENT_CLASS_RE.test(className)) {
        node.classList.remove(className)
      }
    })
  }

  const removeSolidBgClasses = (node) => {
    getClassList(node).forEach((className) => {
      if (SOLID_BG_CLASS_RE.test(className)) {
        node.classList.remove(className)
      }
    })
  }

  const removeSolidBackground = (node) => {
    node.classList.remove('g-bg')
    node.style.removeProperty('--bg')
  }

  const ensureSolidBackgroundClass = (node) => {
    if (node.style.getPropertyValue('--bg').trim()) {
      node.classList.add('g-bg')
    }
  }

  const hasSolidBackground = (node) => {
    return node.classList.contains('g-bg') || hasSolidBgClass(node) || !!node.style.getPropertyValue('--bg').trim()
  }

  const getInlineBackgroundImage = (node) => {
    return (node.style.backgroundImage || '').trim()
  }

  const hasInlineImageBackground = (node) => {
    const backgroundImage = getInlineBackgroundImage(node)
    return /url\(/.test(backgroundImage) || /-webkit-image-set\(/.test(backgroundImage)
  }

  const normalizeInlineBackgroundImage = (node) => {
    const backgroundImage = getInlineBackgroundImage(node)

    if (!backgroundImage) {
      return
    }

    const match = /(?:-webkit-image-set\(url|url)\((["']?)(.*?)\1\)(?:\s*1x)?/.exec(backgroundImage)
    const normalizedImage = match && match[2] ? `url("${match[2]}")` : backgroundImage

    node.style.setProperty('background-image', normalizedImage, 'important')
  }

  const applyImageBackgroundExclusivity = (node) => {
    if (!hasInlineImageBackground(node)) {
      return false
    }

    removeGradientClasses(node)
    removeSolidBgClasses(node)
    removeSolidBackground(node)
    node.style.setProperty('background-color', 'transparent', 'important')
    normalizeInlineBackgroundImage(node)

    return true
  }

  const sanitize = (node, mutation) => {
    const target = resolveTarget(node)

    if (!target) {
      return
    }

    if (applyImageBackgroundExclusivity(target)) {
      return
    }

    if (mutation.type === 'class') {
      const addedClasses = getAddedClasses(target, mutation.oldValue)
      const hasNewGradient = addedClasses.some((className) => GRADIENT_CLASS_RE.test(className))

      if (hasNewGradient) {
        removeSolidBackground(target)
        return
      }

      if (hasGradientClass(target) && hasSolidBackground(target) && !target.style.getPropertyValue('--bg').trim()) {
        removeSolidBackground(target)
        return
      }
    }

    if (mutation.type === 'style' && target.style.getPropertyValue('--bg').trim()) {
      removeGradientClasses(target)
      ensureSolidBackgroundClass(target)
    }
  }

  const sanitizeInitialState = () => {
    document.querySelectorAll(TARGET_SELECTOR).forEach((node) => {
      if (applyImageBackgroundExclusivity(node)) {
        return
      }

      if (hasGradientClass(node) && hasSolidBackground(node) && !node.style.getPropertyValue('--bg').trim()) {
        removeSolidBackground(node)
        return
      }

      if (node.style.getPropertyValue('--bg').trim()) {
        removeGradientClasses(node)
        ensureSolidBackgroundClass(node)
      }
    })
  }

  const isRelevantTargetNode = (node) => {
    if (!(node instanceof HTMLElement)) {
      return false
    }

    return node.matches(TARGET_SELECTOR) || !!node.querySelector(TARGET_SELECTOR)
  }

  const mutationTouchesTarget = (mutation) => {
    return Array.from(mutation.addedNodes).some(isRelevantTargetNode) ||
      Array.from(mutation.removedNodes).some(isRelevantTargetNode)
  }

  const boot = () => {
    sanitizeInitialState()

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          if (mutationTouchesTarget(mutation)) {
            window.requestAnimationFrame(sanitizeInitialState)
          }

          return
        }

        if (mutation.type !== 'attributes' || !(mutation.target instanceof HTMLElement)) {
          return
        }

        if (!mutation.target.matches(TARGET_SELECTOR)) {
          return
        }

        const attributeName = mutation.attributeName
        if (attributeName !== 'class' && attributeName !== 'style') {
          return
        }

        window.requestAnimationFrame(() => {
          sanitize(mutation.target, {
            type: attributeName,
            oldValue: mutation.oldValue
          })
        })
      })
    })

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['class', 'style']
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true })
  } else {
    boot()
  }
})()
